import React, {
  useState,
  useRef,
  useLayoutEffect,
  memo,
  useCallback,
  useMemo,
} from "react";
import { useTranslation, Trans } from "react-i18next";
import QuestionTitle from "./QuestionTitle";
import QuestionOptions from "./QuestionOptions";

const QuestionSection = memo(
  ({
    questionNumber,
    title,
    stepDescription,
    options,
    isDisabled,
    isExpanded,
    onSelect,
    selectedValue,
    selectedLabel,
    onToggle,
  }) => {
    const { t } = useTranslation();
    const [contentHeight, setContentHeight] = useState(0);
    const innerRef = useRef(null);

    const optionsKey = useMemo(
      () => (Array.isArray(options) ? options.map((o) => o.value).join("|") : ""),
      [options]
    );

    useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const measure = () => setContentHeight(el.scrollHeight);
      measure();
      requestAnimationFrame(measure);
    }, [optionsKey, isExpanded]);

    const handleSelect = useCallback(
      (value) => {
        onSelect(value);
      },
      [onSelect]
    );

    const isAnswered = Boolean(selectedValue);
    const triggerId = `assessment-step-${questionNumber}-trigger`;
    const panelId = `assessment-step-${questionNumber}-panel`;

    return (
      <div
        className={`services-picker__question vh-accordion-step services-picker__question--${questionNumber} 
        ${isDisabled ? "services-picker__question--disable" : ""} 
        ${isExpanded ? "services-picker__question--expanded" : "services-picker__question--closed"}
        ${isAnswered ? "services-picker__question--answered" : ""}`}
      >
        <QuestionTitle
          number={questionNumber}
          title={title}
          isExpanded={isExpanded}
          onToggle={!isDisabled ? onToggle : undefined}
          selectedLabel={selectedLabel}
          isDisabled={isDisabled}
          panelId={panelId}
          triggerId={triggerId}
          isAnswered={isAnswered}
        />

        <div
          id={panelId}
          role="region"
          aria-labelledby={triggerId}
          aria-hidden={!isExpanded}
          className={`vh-accordion-step__panel ${isExpanded ? "vh-accordion-step__panel--open" : ""}`}
        >
          <div
            className="vh-assessment__question-body vh-accordion-step__panel-anim"
            style={{
              height: isExpanded ? contentHeight : 0,
              overflow: "hidden",
              transition:
                "height 0.45s var(--vh-ease-soft, cubic-bezier(0.4, 0.2, 0.4, 1))",
            }}
          >
            <div ref={innerRef} className="vh-accordion-step__panel-inner">
              <div className="vh-accordion-step__options">
                {isExpanded && stepDescription ? (
                  <p className="vh-accordion-step__step-desc description-n">
                    {stepDescription}
                  </p>
                ) : null}
                {isExpanded && !selectedValue ? (
                  <p className="vh-accordion-step__pick-hint description-n">
                    {t("assessment.pickHint")}
                  </p>
                ) : null}
                <QuestionOptions
                  key={optionsKey}
                  options={options}
                  onSelect={handleSelect}
                  selectedValue={selectedValue}
                />
              </div>
            </div>
          </div>
        </div>

        {isDisabled ? (
          <div className="vh-accordion-step__locked-ribbon" role="status">
            <span className="vh-accordion-step__locked-ribbon-icon" aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 11V9a4 4 0 018 0v2M7 11h10a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1v-6a1 1 0 011-1z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="vh-accordion-step__locked-ribbon-text">
              <Trans
                i18nKey="assessment.lockedStep"
                values={{ previousStep: questionNumber - 1 }}
                components={{ bold: <strong /> }}
              />
            </span>
          </div>
        ) : null}
      </div>
    );
  }
);

QuestionSection.displayName = "QuestionSection";

export default QuestionSection;
